import { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { TrendingUp, ShoppingBag, Users, RefreshCw } from 'lucide-react';
import PerformanceOverlay from './PerformanceOverlay';

/**
 * Real-time today's-sales tile for the POS header.
 *
 * Polls `/api/cashier/today-tile` every 5s using the cashier's bearer
 * token. Shows orders, revenue, active cashiers and a tiny payment-mix
 * sparkline. Falls back to the page-level `stats` object if the polling
 * endpoint is unreachable so the cashier always sees something.
 *
 * The widget is intentionally compact (fits in the header) — for a
 * larger live dashboard, expand on click into the Stats screen.
 */
const POLL_MS = 20000; // new sales refresh instantly via refreshKey

export default function TodayTile({ branchId, cashierToken, fallback, currencySymbol = 'SR', refreshKey }) {
  const [data, setData] = useState(null);
  const [pulse, setPulse] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const lastRevenueRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    let timer = null;

    const tick = async () => {
      // Skip while the tab is hidden or the tile isn't displayed (it is md+ only); keep the 5s cadence.
      if (document.hidden || window.innerWidth < 768) {
        if (!cancelled) timer = setTimeout(tick, POLL_MS);
        return;
      }
      try {
        const headers = cashierToken ? { Authorization: `Bearer ${cashierToken}` } : {};
        const params = branchId ? { branch_id: branchId } : {};
        const r = await api.get('/cashier/today-tile', { headers, params });
        if (cancelled) return;
        const next = r.data;
        // Pulse the revenue chip green for ~1s every time it grows so
        // cashiers visually catch a new sale even from across the room.
        if (lastRevenueRef.current && (next.revenue || 0) > lastRevenueRef.current) {
          setPulse(true);
          setTimeout(() => !cancelled && setPulse(false), 1000);
        }
        lastRevenueRef.current = next.revenue || 0;
        setData(next);
      } catch {
        // Silent — fallback to `fallback` prop. Don't toast every 5s.
      } finally {
        if (!cancelled) timer = setTimeout(tick, POLL_MS);
      }
    };
    // Refresh at once when the tab becomes visible again instead of polling a hidden tab.
    const onVis = () => { if (!document.hidden && !cancelled) { clearTimeout(timer); tick(); } };
    document.addEventListener('visibilitychange', onVis);

    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); document.removeEventListener('visibilitychange', onVis); };
  }, [branchId, cashierToken, refreshKey]);

  // Pull values from live data → fallback chain.
  const orders = data?.orders ?? fallback?.today?.total_orders ?? 0;
  const revenue = data?.revenue ?? fallback?.today?.total_sales ?? 0;
  const activeCashiers = data?.active_cashiers ?? null;
  const paymentMix = data?.payment_mix || [];

  return (
    <>
      <button
        type="button"
        onClick={() => setOverlayOpen(true)}
        className="hidden md:flex items-stretch gap-2 group"
        data-testid="pos-today-tile"
        title={(paymentMix.length ? paymentMix.map(p => `${p.method}: ${currencySymbol} ${p.revenue.toLocaleString()}`).join(' · ') + ' · ' : '') + 'Click for full performance dashboard'}
      >
        <div
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border transition-colors duration-300 group-hover:ring-2 group-hover:ring-emerald-300/50 ${
            pulse ? 'bg-emerald-100 border-emerald-300' : 'bg-success/10 border-transparent'
          }`}
        >
          <TrendingUp size={14} className="text-success" />
          <div className="leading-tight text-left">
            <p className="text-[10px] text-success font-medium">Today</p>
            <p className="font-bold text-sm text-success tabular-nums" data-testid="today-sales">
              {currencySymbol} {revenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </p>
          </div>
          <RefreshCw size={9} className="text-success/40 ml-0.5" style={{ animation: 'spin 5s linear infinite' }} />
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-info/10 rounded-xl group-hover:ring-2 group-hover:ring-sky-300/50 transition-shadow">
          <ShoppingBag size={14} className="text-info" />
          <div className="leading-tight text-left">
            <p className="text-[10px] text-info font-medium">Orders</p>
            <p className="font-bold text-sm text-info tabular-nums" data-testid="today-orders">{orders}</p>
          </div>
        </div>
        {activeCashiers !== null && (
          <div className="hidden lg:flex items-center gap-1.5 px-3 py-1.5 bg-violet-50 dark:bg-violet-950/20 rounded-xl group-hover:ring-2 group-hover:ring-violet-300/50 transition-shadow">
            <Users size={14} className="text-violet-600" />
            <div className="leading-tight text-left">
              <p className="text-[10px] text-violet-600 font-medium">On shift</p>
              <p className="font-bold text-sm text-violet-700 tabular-nums" data-testid="active-cashiers">{activeCashiers}</p>
            </div>
          </div>
        )}
      </button>
      <PerformanceOverlay
        open={overlayOpen}
        onClose={() => setOverlayOpen(false)}
        branchId={branchId}
        cashierToken={cashierToken}
        currencySymbol={currencySymbol}
      />
    </>
  );
}
